import { FREE_PLAN_WATERMARK, assertApprovedLipSyncSettings, assertDigitalHumanCatalog, assertDigitalHumanAssetManifest, resolveResolutionEntitlement, selectLanguageProfile, selectPlatformVolume, } from "../../runtime/lip-sync-contract.js";
export const EXPERTS_ALIVE_SETTINGS_URL = "https://expertsalive.ai/sdk/runtime/approved-lip-sync-settings.json";
export const EXPERTS_ALIVE_DIGITAL_HUMAN_CATALOG_URL = "https://d32q4q90fh3ret.cloudfront.net/digital-humans/sdk/v1/web/catalog.json";
export const EXPERTS_ALIVE_DIGITAL_HUMAN_ASSET_ROOT = "https://d32q4q90fh3ret.cloudfront.net/digital-humans/sdk/v1/humans/";
export function digitalHumanManifestUrl(slug) {
    const normalized = slug.trim();
    if (!/^[a-zA-Z0-9._-]+$/.test(normalized)) {
        throw new Error("Digital Human slug contains unsupported characters.");
    }
    return new URL(`${encodeURIComponent(normalized)}/asset-manifest.json`, EXPERTS_ALIVE_DIGITAL_HUMAN_ASSET_ROOT).href;
}
export async function loadDigitalHumanCatalog(url = EXPERTS_ALIVE_DIGITAL_HUMAN_CATALOG_URL) {
    const response = await fetch(url, { cache: "no-cache" });
    if (!response.ok)
        throw new Error(`Could not load the Digital Human catalog (${response.status}).`);
    const value = await response.json();
    assertDigitalHumanCatalog(value);
    return value;
}
export async function findDigitalHuman(slug) {
    const catalog = await loadDigitalHumanCatalog();
    const human = catalog.humans.find((entry) => entry.slug.toLowerCase() === slug.trim().toLowerCase());
    if (!human)
        throw new Error(`Digital Human ${slug} is not in the approved catalog.`);
    return human;
}
const CACHE_PREFIX = "experts-alive-selected-human-";
const AUDIO_ACTIVITY_START = 0.045;
const AUDIO_ACTIVITY_STOP = 0.018;
const AUDIO_ACTIVITY_HOLD_MS = 240;
const DEFAULT_QA_OUTPUT_SILENCE_HOLD_MS = 650;
const CLOSED_MOUTH_SCORE_THRESHOLD = 0.13;
const SPEAKING_VISUAL_WATCHDOG_INTERVAL_MS = 200;
const SPEAKING_VISUAL_STALL_MS = 700;
const SPEAKING_VISUAL_MIN_ADVANCE_SECONDS = 1 / 120;
/** Deterministic media-clock tracker used by the runtime and regression harness. */
export class SpeakingVisualWatchdogState {
    observedToken = null;
    observedTime = 0;
    advancedAt = 0;
    reset(token, mediaTimeSeconds, nowMs) {
        this.observedToken = token;
        this.observedTime = mediaTimeSeconds;
        this.advancedAt = nowMs;
    }
    clear() {
        this.observedToken = null;
        this.observedTime = 0;
        this.advancedAt = 0;
    }
    forceStall(nowMs) {
        this.advancedAt = nowMs - SPEAKING_VISUAL_STALL_MS;
    }
    observe(token, mediaTimeSeconds, nowMs, visible) {
        if (!visible || this.observedToken !== token) {
            this.reset(token, mediaTimeSeconds, nowMs);
            return null;
        }
        if (Math.abs(mediaTimeSeconds - this.observedTime) >= SPEAKING_VISUAL_MIN_ADVANCE_SECONDS) {
            this.observedTime = mediaTimeSeconds;
            this.advancedAt = nowMs;
            return null;
        }
        const stalledForMs = nowMs - this.advancedAt;
        return stalledForMs >= SPEAKING_VISUAL_STALL_MS ? stalledForMs : null;
    }
}
export function shouldFreezeMouthDuringOutputSilence(enabled, audible, closedPoseScore) {
    return enabled
        && !audible
        && Number.isFinite(closedPoseScore)
        && closedPoseScore < CLOSED_MOUTH_SCORE_THRESHOLD;
}
export function normalizeOutputSilenceHoldMs(value) {
    return Number.isFinite(value)
        ? Math.min(1_500, Math.max(120, Math.round(value)))
        : DEFAULT_QA_OUTPUT_SILENCE_HOLD_MS;
}
/**
 * Legacy cache-only group prefetch. Each member is initialized sequentially so
 * no more than one person's motion package consumes network and decode
 * resources at once. The standard runtime performs the same checksum,
 * version, and Cache API validation used for a visible Digital Human. React
 * applications should prefer ExpertsAliveProvider.preloadGroup(), which also
 * retains decoded runtimes and media surfaces.
 */
export async function prefetchDigitalHumanGroup(options) {
    const members = [...new Map(options.members.map((member) => [member.id, member])).values()];
    if (!members.length)
        throw new Error("Choose at least one Digital Human for the group.");
    const resolution = options.resolution ?? "360x480";
    const host = document.createElement("div");
    host.setAttribute("aria-hidden", "true");
    host.style.cssText = [
        "height:1px",
        "left:-10000px",
        "overflow:hidden",
        "pointer-events:none",
        "position:fixed",
        "top:0",
        `width:${resolution === "360x480" ? 360 : 210}px`,
    ].join(";");
    document.body.append(host);
    try {
        for (const [index, member] of members.entries()) {
            if (!member.id.trim() || !member.manifestUrl.trim()) {
                throw new Error("Every Digital Human group member needs an id and manifest URL.");
            }
            const container = document.createElement("div");
            host.replaceChildren(container);
            const runtime = createExpertsAliveLipSync({
                assetManifestUrl: member.manifestUrl,
                container,
                locale: member.locale || "en-US",
                plan: "enterprise",
                platform: options.platform ?? "webApp",
                resolution: resolution === "360x480" ? "standard" : "small",
                settingsUrl: options.settingsUrl ?? EXPERTS_ALIVE_SETTINGS_URL,
                watermark: false,
            });
            try {
                await runtime.initialize();
                options.onHumanReady?.(member, index + 1, members.length);
            }
            finally {
                runtime.destroy();
            }
        }
    }
    finally {
        host.remove();
    }
}
export class ExpertsAliveLipSyncRuntime {
    options;
    container;
    state = "uninitialized";
    settings = null;
    manifest = null;
    languageProfile = null;
    resolution = null;
    volumeScale = null;
    stage = null;
    carrierVideo = null;
    speakingVideo = null;
    speakingStandbyVideo = null;
    speakingLoopTimer = null;
    speakingLoopTransitionInFlight = false;
    speakingLoopEpoch = 0;
    speakingVisualWatchdogTimer = null;
    speakingVisualProgress = new SpeakingVisualWatchdogState();
    speakingVisualWatchdogTickAt = 0;
    speakingVisualRecoveryInFlight = false;
    posterImage = null;
    transitionCanvas = null;
    transitionFrameClearTimer = null;
    audioElement = null;
    audioListeners = [];
    runtimeListeners = [];
    blobUrls = [];
    resetTimer = null;
    audioHoldTimer = null;
    holdSpeakingThroughSilence = false;
    mouthHeldClosedForOutputSilence = false;
    continuousFrameFeatures = [];
    guidedMouthPoses = ["closed"];
    guidedPoseCursor = 0;
    lastGuidedTranscript = "";
    lastGuidedPoseAdvance = 0;
    smoothedContinuousRate = 0.82;
    motionPhaseOffsetSeconds = 0;
    audioSyncFrame = null;
    analyserFrame = null;
    audioContext = null;
    analyser = null;
    analyserData = null;
    inputStream = null;
    inputSource = null;
    inputAudioContext = null;
    inputAnalyser = null;
    inputAnalyserData = null;
    inputAnalyserFrame = null;
    inputTrackListeners = [];
    stopInputTracksOnDetach = false;
    lastInputLevelEventAt = 0;
    resizeObserver = null;
    resolutionQueue = Promise.resolve();
    pendingResolution = null;
    visualTransitionEpoch = 0;
    destroyed = false;
    usesSharedNeutralLoop() {
        return this.manifest?.idleTransitionMode === "shared-neutral-loop-v1";
    }
    constructor(options) {
        const container = typeof options.container === "string"
            ? document.querySelector(options.container)
            : options.container;
        if (!container)
            throw new Error("Experts Alive could not find the lip-sync container.");
        this.container = container;
        this.options = {
            ...options,
            plan: options.plan ?? "free",
            bandwidthSaver: options.bandwidthSaver ?? false,
            resolution: options.bandwidthSaver ? "small" : (options.resolution ?? "auto"),
            autoUpgradeWidth: options.autoUpgradeWidth ?? 280,
            autoDowngradeWidth: options.autoDowngradeWidth ?? 240,
            honorBrowserDataSaver: options.honorBrowserDataSaver ?? true,
            locale: options.locale ?? "en-US",
            platform: options.platform ?? "html",
            className: options.className ?? "",
            watermark: options.watermark ?? true,
            watermarkText: options.watermarkText?.trim() || FREE_PLAN_WATERMARK.text,
        };
        if (this.options.autoDowngradeWidth >= this.options.autoUpgradeWidth) {
            throw new Error("autoDowngradeWidth must be lower than autoUpgradeWidth.");
        }
    }
    async initialize() {
        this.assertActive();
        if (this.state !== "uninitialized")
            throw new Error("The lip-sync runtime can only be initialized once.");
        this.transition("loading");
        try {
            const [settingsValue, manifestValue] = await Promise.all([
                this.fetchJson(this.options.settingsUrl),
                this.fetchJson(this.options.assetManifestUrl),
            ]);
            assertApprovedLipSyncSettings(settingsValue);
            assertDigitalHumanAssetManifest(manifestValue);
            this.settings = settingsValue;
            this.manifest = manifestValue;
            this.languageProfile = selectLanguageProfile(this.settings, this.options.locale);
            this.volumeScale = selectPlatformVolume(this.settings, this.options.platform).volumeScale;
            this.resolution = this.resolveResolutionForWidth(this.container.getBoundingClientRect().width);
            const variant = this.manifest.variants[this.resolution];
            if (variant.referenceOnly)
                throw new Error(`${this.resolution} is reference-only and cannot ship.`);
            const cacheName = this.cacheName(this.manifest);
            const urls = await this.loadVariant(cacheName, variant);
            await this.configureContinuousMotion(urls.frameIndex);
            await this.pruneObsoleteHumanCaches(this.manifest, cacheName);
            await this.mountVariant(variant, urls);
            this.transition("idle");
            this.startResizeObserver();
            const snapshot = this.getSnapshot();
            this.options.onReady?.(snapshot);
            this.emit("ready", {
                humanId: snapshot.humanId,
                resolution: snapshot.resolution,
                locale: snapshot.locale,
            });
            return snapshot;
        }
        catch (cause) {
            const error = cause instanceof Error ? cause : new Error(String(cause));
            this.transition("error");
            this.options.onError?.(error);
            this.emit("error", { message: error.message });
            throw error;
        }
    }
    getSnapshot() {
        return {
            state: this.state,
            humanId: this.manifest?.digitalHumanId ?? null,
            resolution: this.resolution,
            locale: this.languageProfile?.locale ?? null,
            volumeScale: this.volumeScale,
            manifestVersion: this.manifest?.manifestVersion ?? null,
            resolutionMode: this.options.resolution,
            containerWidth: Math.round(this.container.getBoundingClientRect().width),
            microphoneActive: this.inputStream !== null,
        };
    }
    getApprovedSettings() {
        return this.settings;
    }
    setLocale(locale) {
        this.assertReady();
        this.languageProfile = selectLanguageProfile(this.settings, locale);
        if (this.speakingVideo)
            this.speakingVideo.playbackRate = this.effectiveSpeakingRate();
        if (this.speakingStandbyVideo) {
            this.speakingStandbyVideo.playbackRate = this.effectiveSpeakingRate();
        }
        this.emit("locale-change", {
            requestedLocale: locale,
            selectedLocale: this.languageProfile.locale,
            motionRate: this.languageProfile.motionRate,
        });
        return this.languageProfile;
    }
    async setResolutionMode(mode) {
        this.assertReady();
        this.options.resolution = mode;
        this.options.bandwidthSaver = mode === "small";
        this.startResizeObserver();
        const desired = this.resolveResolutionForWidth(this.container.getBoundingClientRect().width);
        await this.queueResolutionChange(desired, "host-policy-change");
        return this.resolution;
    }
    async reevaluateViewSize(width = this.container.getBoundingClientRect().width) {
        this.assertReady();
        const desired = this.resolveResolutionForWidth(width);
        await this.queueResolutionChange(desired, "host-view-size");
        return this.resolution;
    }
    attachAudioElement(audio) {
        this.assertReady();
        this.detachAudioElement();
        this.audioElement = audio;
        audio.volume = Math.min(1, Math.max(0, this.volumeScale ?? 0.6));
        const listen = (event, handler) => {
            audio.addEventListener(event, handler);
            this.audioListeners.push(() => audio.removeEventListener(event, handler));
        };
        const beginFromAudio = () => {
            if (this.audioElement !== audio || audio.paused || audio.ended)
                return;
            this.beginSpeaking({ audioTimeSeconds: audio.currentTime, source: "audio-element" });
        };
        const stopFromAudio = (reason) => {
            if (this.audioElement !== audio)
                return;
            this.returnToIdle(reason);
        };
        listen("playing", beginFromAudio);
        listen("pause", () => {
            if (!audio.ended && this.state === "speaking")
                stopFromAudio("audio-paused");
        });
        listen("ended", () => stopFromAudio("audio-ended"));
        listen("abort", () => stopFromAudio("audio-aborted"));
        listen("emptied", () => stopFromAudio("audio-emptied"));
        listen("seeking", () => this.syncMotionToAudio(true));
        listen("error", () => {
            stopFromAudio("audio-error");
            this.emit("audio-error");
        });
        this.emit("audio-attached", { volumeScale: this.volumeScale ?? 0.6 });
        // A client can attach audio before React finishes rendering or before an
        // SDK callback runs. Do not wait for a second `playing` event in that case.
        if (!audio.paused && !audio.ended && audio.readyState >= 2)
            beginFromAudio();
        // A stale cleanup from audio A must never detach a newer audio B.
        return () => {
            if (this.audioElement === audio)
                this.detachAudioElement();
        };
    }
    attachMediaStream(stream) {
        return this.attachOutputMediaStream(stream);
    }
    /** Attach a host-provided output stream whose audio should drive Emma's mouth. */
    attachOutputMediaStream(stream) {
        this.assertReady();
        this.detachAnalyser();
        const AudioContextConstructor = window.AudioContext
            ?? window.webkitAudioContext;
        if (!AudioContextConstructor)
            throw new Error("Web Audio is not available in this browser.");
        const context = new AudioContextConstructor();
        const source = context.createMediaStreamSource(stream);
        const analyser = context.createAnalyser();
        analyser.fftSize = 512;
        analyser.smoothingTimeConstant = 0.45;
        source.connect(analyser);
        this.audioContext = context;
        this.analyser = analyser;
        this.analyserData = new Float32Array(analyser.fftSize);
        const sample = () => {
            if (!this.analyser || !this.analyserData || this.destroyed)
                return;
            this.analyser.getFloatTimeDomainData(this.analyserData);
            let sum = 0;
            for (const value of this.analyserData)
                sum += value * value;
            this.setAudioLevel(Math.sqrt(sum / this.analyserData.length));
            this.analyserFrame = requestAnimationFrame(sample);
        };
        sample();
        this.emit("media-stream-attached");
        return () => this.detachAnalyser();
    }
    /**
     * Request microphone access and begin local input metering. The microphone
     * never drives the Digital Human's mouth and is not uploaded by this runtime.
     */
    async startListening(constraints = {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
    }) {
        this.assertReady();
        if (this.inputStream) {
            if (this.state !== "listening")
                this.transition("listening");
            return this.inputStream;
        }
        if (!navigator.mediaDevices?.getUserMedia) {
            const error = new Error("Microphone capture is not available in this browser.");
            this.options.onError?.(error);
            this.emit("listening-error", { message: error.message });
            throw error;
        }
        this.prepareForListening();
        this.emit("listening-requested");
        let requestedStream = null;
        try {
            requestedStream = await navigator.mediaDevices.getUserMedia({ audio: constraints, video: false });
            this.assertActive();
            this.attachInputMediaStream(requestedStream, true);
            return requestedStream;
        }
        catch (cause) {
            if (requestedStream && this.inputStream !== requestedStream) {
                for (const track of requestedStream.getTracks())
                    track.stop();
            }
            const error = cause instanceof Error ? cause : new Error(String(cause));
            if (!this.destroyed && this.state !== "idle")
                this.transition("idle");
            this.options.onInputLevel?.(0);
            this.options.onError?.(error);
            this.emit("listening-error", { message: error.message, name: error.name });
            throw error;
        }
    }
    /** Attach a microphone stream supplied by the host application. */
    attachInputMediaStream(stream, stopTracksOnDetach = false) {
        this.assertReady();
        if (stream.getAudioTracks().length === 0) {
            throw new Error("The input MediaStream does not contain an audio track.");
        }
        this.prepareForListening();
        this.detachInputCapture(true);
        const AudioContextConstructor = window.AudioContext
            ?? window.webkitAudioContext;
        if (!AudioContextConstructor)
            throw new Error("Web Audio is not available in this browser.");
        const context = new AudioContextConstructor();
        const source = context.createMediaStreamSource(stream);
        const analyser = context.createAnalyser();
        analyser.fftSize = 512;
        analyser.smoothingTimeConstant = 0.45;
        source.connect(analyser);
        this.inputStream = stream;
        this.inputSource = source;
        this.inputAudioContext = context;
        this.inputAnalyser = analyser;
        this.inputAnalyserData = new Float32Array(analyser.fftSize);
        this.stopInputTracksOnDetach = stopTracksOnDetach;
        void context.resume().catch(() => undefined);
        const handleTrackEnded = () => {
            if (this.inputStream === stream)
                this.stopListening("input-track-ended");
        };
        for (const track of stream.getAudioTracks()) {
            track.addEventListener("ended", handleTrackEnded);
            this.inputTrackListeners.push(() => track.removeEventListener("ended", handleTrackEnded));
        }
        const sample = () => {
            if (!this.inputAnalyser || !this.inputAnalyserData || this.destroyed)
                return;
            this.inputAnalyser.getFloatTimeDomainData(this.inputAnalyserData);
            let sum = 0;
            for (const value of this.inputAnalyserData)
                sum += value * value;
            const rms = Math.min(1, Math.sqrt(sum / this.inputAnalyserData.length));
            this.options.onInputLevel?.(rms);
            const now = performance.now();
            if (now - this.lastInputLevelEventAt >= 125) {
                this.lastInputLevelEventAt = now;
                this.emit("input-level", { level: rms });
            }
            this.inputAnalyserFrame = requestAnimationFrame(sample);
        };
        sample();
        this.transition("listening");
        this.emit("listening-start", {
            source: stopTracksOnDetach ? "microphone-request" : "host-media-stream",
            tracks: stream.getAudioTracks().length,
        });
        return () => {
            if (this.inputStream === stream)
                this.stopListening("input-detached");
        };
    }
    stopListening(reason = "host-stop") {
        this.assertReady();
        const wasListening = this.state === "listening" || this.inputStream !== null;
        this.detachInputCapture(true);
        if (this.state === "listening")
            this.transition("idle");
        if (wasListening)
            this.emit("listening-stop", { reason });
    }
    setAudioLevel(level) {
        this.assertReady();
        const normalized = Math.min(1, Math.max(0, Number.isFinite(level) ? level : 0));
        this.emit("audio-level", { level: normalized });
        this.updateSpeakingAudioLevel(normalized);
        if (normalized >= AUDIO_ACTIVITY_START) {
            this.clearAudioHold();
            if (this.state === "idle" || this.state === "interrupted") {
                this.beginSpeaking({ source: "audio-level" });
            }
            return;
        }
        if (this.holdSpeakingThroughSilence && !this.options.freezeMouthDuringOutputSilence)
            return;
        if (normalized <= AUDIO_ACTIVITY_STOP && this.state === "speaking" && this.audioHoldTimer === null) {
            this.audioHoldTimer = window.setTimeout(() => {
                this.audioHoldTimer = null;
                if (!this.audioElement || this.audioElement.paused)
                    this.returnToIdle("audio-level-silence");
            }, this.options.freezeMouthDuringOutputSilence
                ? normalizeOutputSilenceHoldMs(this.options.outputSilenceHoldMs)
                : AUDIO_ACTIVITY_HOLD_MS);
        }
    }
    /** Update the growing assistant transcript without delaying Realtime audio. */
    updateSpeechGuide(transcript) {
        if (!this.continuousFrameFeatures.length)
            return;
        const trimmed = transcript.trim();
        if (!trimmed) {
            this.guidedMouthPoses = ["closed"];
            this.guidedPoseCursor = 0;
            this.lastGuidedTranscript = "";
            return;
        }
        const isGrowth = trimmed.startsWith(this.lastGuidedTranscript);
        this.guidedMouthPoses = this.guidedMouthProgram(trimmed);
        this.guidedPoseCursor = isGrowth
            ? Math.min(this.guidedPoseCursor, Math.max(0, this.guidedMouthPoses.length - 1))
            : 0;
        this.lastGuidedTranscript = trimmed;
    }
    /** Guide sequential full-frame playback from audio rendered by the browser. */
    updateSpeakingAudioLevel(level) {
        if (this.state !== "speaking" || !this.speakingVideo || !this.continuousFrameFeatures.length)
            return;
        const energy = Math.min(1, Math.max(0, Number.isFinite(level) ? level : 0));
        const audible = energy > 0.025;
        if (audible && this.mouthHeldClosedForOutputSilence) {
            this.mouthHeldClosedForOutputSilence = false;
            const video = this.speakingVideo;
            video.playbackRate = Math.max(0.56, this.smoothedContinuousRate);
            this.speakingVisualProgress.reset(video, video.currentTime, performance.now());
            void video.play()
                .then(() => {
                if (this.destroyed || this.state !== "speaking" || this.speakingVideo !== video)
                    return;
                this.scheduleSpeakingLoop();
                this.emit("output-silence-mouth-resumed");
            })
                .catch((cause) => {
                const error = cause instanceof Error ? cause : new Error(String(cause));
                this.options.onError?.(error);
                this.emit("playback-blocked", { message: error.message });
            });
        }
        const now = performance.now();
        const cadenceMs = Math.max(75, 115 - energy * 35);
        if (audible
            && now - this.lastGuidedPoseAdvance >= cadenceMs
            && this.guidedPoseCursor + 1 < this.guidedMouthPoses.length) {
            this.guidedPoseCursor += 1;
            this.lastGuidedPoseAdvance = now;
        }
        let target;
        if (audible
            && this.lastGuidedTranscript
            && this.guidedPoseCursor < this.guidedMouthPoses.length - 1) {
            target = this.guidedMouthPoses[this.guidedPoseCursor] ?? "rest";
        }
        else if (audible) {
            target = this.fallbackPose(energy, Math.floor(now / 125));
        }
        else {
            target = "closed";
        }
        const frameCount = this.continuousFrameFeatures.length;
        const framesPerSecond = this.manifest?.continuousMotion?.framesPerSecond ?? 30;
        const currentIndex = Math.floor(Math.max(0, this.speakingVideo.currentTime) * framesPerSecond) % frameCount;
        let bestOffset = 0;
        const currentPoseScore = this.poseScore(this.continuousFrameFeatures[currentIndex], target);
        let bestScore = currentPoseScore;
        for (let offset = 1; offset <= Math.min(12, frameCount - 1); offset += 1) {
            const candidateIndex = (currentIndex + offset) % frameCount;
            const score = this.poseScore(this.continuousFrameFeatures[candidateIndex], target);
            if (score < bestScore) {
                bestScore = score;
                bestOffset = offset;
            }
        }
        if (shouldFreezeMouthDuringOutputSilence(this.options.freezeMouthDuringOutputSilence ?? false, audible, currentPoseScore)) {
            if (!this.mouthHeldClosedForOutputSilence) {
                this.mouthHeldClosedForOutputSilence = true;
                this.clearSpeakingLoop();
                this.speakingVideo.pause();
                this.speakingStandbyVideo?.pause();
                this.speakingVisualProgress.reset(this.speakingVideo, this.speakingVideo.currentTime, now);
                this.emit("output-silence-mouth-held", { closedPoseScore: currentPoseScore });
            }
            return;
        }
        let desiredRate;
        if (audible) {
            desiredRate = bestScore < 0.15
                ? 0.56 + energy * 0.2
                : Math.min(1.22, 0.84 + bestOffset * 0.03 + energy * 0.08);
        }
        else {
            desiredRate = bestScore < 0.13
                ? 0.28
                : Math.min(1.05, 0.74 + bestOffset * 0.025);
        }
        const response = desiredRate > this.smoothedContinuousRate ? 0.34 : 0.22;
        this.smoothedContinuousRate += (desiredRate - this.smoothedContinuousRate) * response;
        this.smoothedContinuousRate = Math.min(1.22, Math.max(0.28, this.smoothedContinuousRate));
        this.speakingVideo.playbackRate = this.smoothedContinuousRate;
        if (this.speakingStandbyVideo)
            this.speakingStandbyVideo.playbackRate = this.smoothedContinuousRate;
    }
    beginSpeaking(options = {}) {
        this.assertReady();
        if (!this.speakingVideo || !this.languageProfile)
            return;
        this.mouthHeldClosedForOutputSilence = false;
        if (options.holdThroughSilence)
            this.holdSpeakingThroughSilence = true;
        if (this.state === "speaking"
            && !this.speakingVideo.paused
            && options.audioTimeSeconds === undefined) {
            this.speakingVideo.style.opacity = "1";
            if (this.transitionCanvas)
                this.transitionCanvas.style.opacity = "0";
            this.startAudioSyncLoop();
            this.scheduleSpeakingLoop();
            this.startSpeakingVisualWatchdog();
            return;
        }
        if (this.inputStream) {
            this.detachInputCapture(true);
            this.emit("listening-stop", { reason: "output-speaking" });
        }
        this.clearResetTimer();
        this.clearAudioHold();
        const profile = this.languageProfile;
        const motion = this.speakingMotionWindow(this.speakingVideo, profile);
        const audioTime = options.audioTimeSeconds ?? this.audioElement?.currentTime ?? 0;
        const speakingVideo = this.speakingVideo;
        this.clearSpeakingLoop();
        if (this.speakingStandbyVideo) {
            this.speakingStandbyVideo.pause();
            this.speakingStandbyVideo.style.opacity = "0";
            this.speakingStandbyVideo.style.zIndex = "2";
        }
        speakingVideo.style.zIndex = "3";
        const transitionEpoch = ++this.visualTransitionEpoch;
        this.holdRenderedFrame(this.carrierVideo);
        speakingVideo.style.opacity = "0";
        const alignedSpeakingTime = this.phaseAlignedSpeakingTime(motion, audioTime, profile.motionRate);
        const useExactTransitionAnchor = Number.isFinite(this.manifest?.transitionAnchorSeconds);
        speakingVideo.playbackRate = this.effectiveSpeakingRate();
        if (this.state !== "speaking")
            this.transition("speaking");
        this.emit("speaking-start", { source: options.source ?? "host", locale: profile.locale });
        if (useExactTransitionAnchor) {
            speakingVideo.pause();
            void this.seekVideoForTransition(speakingVideo, alignedSpeakingTime)
                .then(() => this.waitForPaintedVideoFrame(speakingVideo))
                .then(() => this.nextAnimationFrame())
                .then(() => {
                if (this.destroyed
                    || transitionEpoch !== this.visualTransitionEpoch
                    || this.state !== "speaking"
                    || this.speakingVideo !== speakingVideo)
                    return;
                const videoTransition = speakingVideo.style.transition;
                speakingVideo.style.transition = "none";
                speakingVideo.style.opacity = "1";
                const canvas = this.transitionCanvas;
                if (canvas) {
                    this.clearTransitionFrameTimer();
                    canvas.style.transition = "none";
                    canvas.style.opacity = "0";
                    canvas.getContext("2d")?.clearRect(0, 0, canvas.width, canvas.height);
                }
                void speakingVideo.offsetWidth;
                speakingVideo.style.transition = videoTransition;
                this.emit("speaking-frame-ready", { source: options.source ?? "host" });
                return speakingVideo.play();
            })
                .then(() => {
                if (this.destroyed
                    || transitionEpoch !== this.visualTransitionEpoch
                    || this.state !== "speaking"
                    || this.speakingVideo !== speakingVideo)
                    return;
                this.startAudioSyncLoop();
                this.scheduleSpeakingLoop();
                this.startSpeakingVisualWatchdog();
            })
                .catch((cause) => {
                const error = cause instanceof Error ? cause : new Error(String(cause));
                this.options.onError?.(error);
                this.emit("playback-blocked", { message: error.message });
            });
            return;
        }
        speakingVideo.currentTime = alignedSpeakingTime;
        this.startAudioSyncLoop();
        void speakingVideo.play()
            .then(() => this.waitForPaintedVideoFrame(speakingVideo))
            .then(() => this.nextAnimationFrame())
            .then(() => {
            if (this.destroyed
                || transitionEpoch !== this.visualTransitionEpoch
                || this.state !== "speaking"
                || this.speakingVideo !== speakingVideo)
                return;
            speakingVideo.style.opacity = "1";
            this.releaseHeldFrame(transitionEpoch);
            this.emit("speaking-frame-ready", { source: options.source ?? "host" });
            this.scheduleSpeakingLoop();
            this.startSpeakingVisualWatchdog();
        })
            .catch((cause) => {
            const error = cause instanceof Error ? cause : new Error(String(cause));
            this.options.onError?.(error);
            this.emit("playback-blocked", { message: error.message });
        });
    }
    returnToIdle(reason = "complete") {
        if (this.destroyed || !this.speakingVideo || !this.languageProfile)
            return;
        this.holdSpeakingThroughSilence = false;
        this.mouthHeldClosedForOutputSilence = false;
        this.updateSpeechGuide("");
        if (this.state === "listening" || this.state === "idle")
            return;
        this.clearAudioHold();
        this.clearSpeakingLoop();
        this.clearSpeakingVisualWatchdog();
        this.stopAudioSyncLoop();
        const transitionEpoch = ++this.visualTransitionEpoch;
        this.holdRenderedFrame(this.speakingVideo);
        this.speakingVideo.style.opacity = "0";
        if (this.speakingStandbyVideo)
            this.speakingStandbyVideo.style.opacity = "0";
        this.releaseHeldFrameAfterCarrierPaint(transitionEpoch, this.speakingVideo.currentTime);
        const video = this.speakingVideo;
        const start = this.speakingMotionWindow(video, this.languageProfile).start;
        const standby = this.speakingStandbyVideo;
        window.setTimeout(() => {
            if (this.destroyed || this.state === "speaking")
                return;
            video.pause();
            video.currentTime = start;
            if (standby && this.languageProfile) {
                standby.pause();
                standby.currentTime = this.speakingMotionWindow(standby, this.languageProfile).start;
            }
        }, this.visualTransitionMs() + 30);
        this.transition("idle");
        this.emit("speaking-stop", { reason });
    }
    /** Immediately sanitize a decoded runtime before another lease can own it. */
    resetForReuse(reason = "host-reuse") {
        if (this.destroyed || !this.speakingVideo || !this.languageProfile)
            return;
        this.holdSpeakingThroughSilence = false;
        this.mouthHeldClosedForOutputSilence = false;
        this.motionPhaseOffsetSeconds = 0;
        this.clearResetTimer();
        this.clearAudioHold();
        this.clearSpeakingLoop();
        this.clearSpeakingVisualWatchdog();
        this.clearTransitionFrameTimer();
        this.stopAudioSyncLoop();
        this.detachAudioElement(false);
        if (this.inputStream)
            this.detachInputCapture(true);
        this.visualTransitionEpoch += 1;
        const speaking = this.speakingVideo;
        speaking.pause();
        speaking.currentTime = this.speakingMotionWindow(speaking, this.languageProfile).start;
        speaking.style.opacity = "0";
        if (this.speakingStandbyVideo) {
            this.speakingStandbyVideo.pause();
            this.speakingStandbyVideo.currentTime = this.speakingMotionWindow(this.speakingStandbyVideo, this.languageProfile).start;
            this.speakingStandbyVideo.style.opacity = "0";
        }
        if (this.transitionCanvas) {
            this.transitionCanvas.style.transition = "none";
            this.transitionCanvas.style.opacity = "0";
            this.transitionCanvas.getContext("2d")?.clearRect(0, 0, this.transitionCanvas.width, this.transitionCanvas.height);
        }
        if (this.posterImage)
            this.posterImage.style.opacity = "0";
        if (this.carrierVideo) {
            this.carrierVideo.style.opacity = "1";
            if (this.usesSharedNeutralLoop()) {
                this.carrierVideo.pause();
                this.carrierVideo.currentTime = 0;
            }
            else {
                void this.carrierVideo.play().catch(() => undefined);
            }
        }
        if (this.state !== "idle")
            this.transition("idle");
        this.emit("runtime-reused", { reason });
    }
    interrupt(reason = "host-interruption", pauseAttachedAudio = true) {
        this.assertReady();
        this.holdSpeakingThroughSilence = false;
        this.mouthHeldClosedForOutputSilence = false;
        if (this.state === "listening" || this.inputStream) {
            this.stopListening(reason);
            return;
        }
        this.clearAudioHold();
        this.clearSpeakingLoop();
        this.clearSpeakingVisualWatchdog();
        this.stopAudioSyncLoop();
        const transitionEpoch = ++this.visualTransitionEpoch;
        this.holdRenderedFrame(this.speakingVideo);
        if (pauseAttachedAudio && this.audioElement && !this.audioElement.paused)
            this.audioElement.pause();
        if (this.speakingVideo)
            this.speakingVideo.style.opacity = "0";
        if (this.speakingStandbyVideo)
            this.speakingStandbyVideo.style.opacity = "0";
        this.releaseHeldFrameAfterCarrierPaint(transitionEpoch, this.speakingVideo?.currentTime);
        this.transition("interrupted");
        this.emit("interrupted", { reason });
        this.clearResetTimer();
        this.resetTimer = window.setTimeout(() => {
            this.resetTimer = null;
            if (this.destroyed || !this.speakingVideo || !this.languageProfile)
                return;
            this.speakingVideo.pause();
            this.speakingVideo.currentTime = this.speakingMotionWindow(this.speakingVideo, this.languageProfile).start;
            if (this.speakingStandbyVideo) {
                this.speakingStandbyVideo.pause();
                this.speakingStandbyVideo.currentTime = this.speakingMotionWindow(this.speakingStandbyVideo, this.languageProfile).start;
            }
            this.transition("idle");
            this.emit("idle-restored", { reason });
        }, Math.max(this.manifest?.resetDelayMs ?? 0, this.visualTransitionMs() + 30));
    }
    destroy() {
        if (this.destroyed)
            return;
        this.destroyed = true;
        this.mouthHeldClosedForOutputSilence = false;
        this.resetContinuousMotion();
        this.visualTransitionEpoch += 1;
        this.clearResetTimer();
        this.clearAudioHold();
        this.clearSpeakingLoop();
        this.clearSpeakingVisualWatchdog();
        this.clearTransitionFrameTimer();
        this.stopAudioSyncLoop();
        this.resizeObserver?.disconnect();
        this.resizeObserver = null;
        this.audioElement?.pause();
        this.detachAudioElement();
        this.detachAnalyser();
        const hadInput = this.inputStream !== null;
        this.detachInputCapture(true);
        if (hadInput)
            this.emit("listening-stop", { reason: "destroyed" });
        for (const remove of this.runtimeListeners.splice(0))
            remove();
        this.carrierVideo?.pause();
        this.speakingVideo?.pause();
        this.speakingStandbyVideo?.pause();
        for (const url of this.blobUrls)
            URL.revokeObjectURL(url);
        this.blobUrls = [];
        this.stage?.remove();
        this.stage = null;
        this.carrierVideo = null;
        this.speakingVideo = null;
        this.speakingStandbyVideo = null;
        this.posterImage = null;
        this.transitionCanvas = null;
        this.transition("destroyed");
        this.emit("destroyed");
    }
    async fetchJson(url) {
        const response = await fetch(url, { cache: "no-store", credentials: "omit" });
        if (!response.ok)
            throw new Error(`Could not load ${url} (${response.status}).`);
        return response.json();
    }
    cacheName(manifest) {
        const humanId = manifest.digitalHumanId.replace(/[^a-zA-Z0-9._-]/g, "-");
        const version = manifest.manifestVersion.replace(/[^a-zA-Z0-9._-]/g, "-");
        return `${CACHE_PREFIX}${humanId}-${version}`;
    }
    resolveResolutionForWidth(width) {
        if (this.options.plan === "free" || this.options.bandwidthSaver || this.options.resolution === "small") {
            return resolveResolutionEntitlement(this.options.plan, true).resolution;
        }
        if (this.options.resolution === "standard")
            return "360x480";
        if (this.options.honorBrowserDataSaver && this.browserRequestsDataSaving())
            return "210x280";
        if (this.resolution === "360x480") {
            return width <= this.options.autoDowngradeWidth ? "210x280" : "360x480";
        }
        return width >= this.options.autoUpgradeWidth ? "360x480" : "210x280";
    }
    browserRequestsDataSaving() {
        const connection = navigator.connection;
        return connection?.saveData === true || connection?.effectiveType === "slow-2g" || connection?.effectiveType === "2g";
    }
    startResizeObserver() {
        this.resizeObserver?.disconnect();
        this.resizeObserver = null;
        if (this.destroyed
            || this.options.plan === "free"
            || this.options.resolution !== "auto"
            || typeof ResizeObserver === "undefined")
            return;
        this.resizeObserver = new ResizeObserver((entries) => {
            const width = entries[0]?.contentRect.width ?? this.container.getBoundingClientRect().width;
            const desired = this.resolveResolutionForWidth(width);
            void this.queueResolutionChange(desired, "automatic-view-size");
        });
        this.resizeObserver.observe(this.container);
    }
    async queueResolutionChange(desired, reason) {
        if (this.destroyed)
            return;
        if (desired === this.resolution) {
            this.pendingResolution = null;
            return;
        }
        if (desired === this.pendingResolution)
            return;
        this.pendingResolution = desired;
        this.resolutionQueue = this.resolutionQueue.then(async () => {
            const target = this.pendingResolution;
            this.pendingResolution = null;
            if (!target || target === this.resolution || this.destroyed)
                return;
            await this.applyResolutionChange(target, reason);
        });
        await this.resolutionQueue;
    }
    async applyResolutionChange(desired, reason) {
        if (!this.manifest || !this.resolution)
            return;
        const previousResolution = this.resolution;
        const wasListening = this.state === "listening";
        const previousBlobUrls = this.blobUrls;
        this.blobUrls = [];
        this.clearResetTimer();
        this.clearAudioHold();
        this.clearSpeakingLoop();
        this.clearSpeakingVisualWatchdog();
        this.stopAudioSyncLoop();
        if (this.audioElement && !this.audioElement.paused)
            this.audioElement.pause();
        this.speakingVideo?.pause();
        this.speakingStandbyVideo?.pause();
        this.transition("loading");
        try {
            const variant = this.manifest.variants[desired];
            const urls = await this.loadVariant(this.cacheName(this.manifest), variant);
            await this.configureContinuousMotion(urls.frameIndex);
            await this.mountVariant(variant, urls);
            for (const url of previousBlobUrls)
                URL.revokeObjectURL(url);
            this.resolution = desired;
            this.transition(wasListening ? "listening" : "idle");
            this.options.onResolutionChange?.(desired, previousResolution);
            this.emit("resolution-change", {
                previous: previousResolution,
                resolution: desired,
                reason,
                containerWidth: Math.round(this.container.getBoundingClientRect().width),
            });
        }
        catch (cause) {
            for (const url of this.blobUrls)
                URL.revokeObjectURL(url);
            this.blobUrls = previousBlobUrls;
            this.resolution = previousResolution;
            this.transition(wasListening ? "listening" : "idle");
            const error = cause instanceof Error ? cause : new Error(String(cause));
            this.options.onError?.(error);
            this.emit("resolution-change-error", { message: error.message, attempted: desired });
        }
    }
    async pruneObsoleteHumanCaches(manifest, activeCacheName) {
        if (!("caches" in window))
            return;
        const humanId = manifest.digitalHumanId.replace(/[^a-zA-Z0-9._-]/g, "-");
        const humanPrefix = `${CACHE_PREFIX}${humanId}-`;
        const names = await caches.keys();
        await Promise.all(names
            .filter((name) => name.startsWith(humanPrefix) && name !== activeCacheName)
            .map((name) => caches.delete(name)));
    }
    async loadVariant(cacheName, variant) {
        const descriptors = [
            ["carrier", variant.carrier],
            ["speaking", variant.speaking],
            ["poster", variant.poster],
        ];
        if (this.manifest?.continuousMotion) {
            descriptors.push(["frameIndex", this.manifest.continuousMotion.frameIndex]);
        }
        const totalBytes = descriptors.reduce((sum, [, asset]) => sum + asset.bytes, 0);
        let loadedBytes = 0;
        const output = {};
        for (const [name, descriptor] of descriptors) {
            output[name] = await this.loadAsset(cacheName, descriptor);
            loadedBytes += descriptor.bytes;
            this.options.onProgress?.({
                asset: name,
                loadedBytes,
                totalBytes,
                fromCache: output[name].fromCache,
            });
            this.emit("asset-ready", {
                asset: name,
                loadedBytes,
                totalBytes,
                fromCache: output[name].fromCache,
            });
        }
        return output;
    }
    async loadAsset(cacheName, asset) {
        const manifestUrl = new URL(this.options.assetManifestUrl, document.baseURI).href;
        const assetUrl = new URL(asset.path, manifestUrl).href;
        const markerUrl = new URL(assetUrl);
        markerUrl.searchParams.set("__ea_verified_sha256", asset.sha256);
        const canCache = "caches" in window && window.isSecureContext;
        const cache = canCache ? await caches.open(cacheName) : null;
        const cached = cache ? await cache.match(assetUrl) : undefined;
        const marker = cache ? await cache.match(markerUrl.href) : undefined;
        if (cached && marker) {
            const blobUrl = URL.createObjectURL(await cached.blob());
            this.blobUrls.push(blobUrl);
            return { blobUrl, fromCache: true };
        }
        const response = cached ?? await fetch(assetUrl, { cache: "no-store", credentials: "omit" });
        if (!response.ok)
            throw new Error(`Could not download ${asset.path} (${response.status}).`);
        const buffer = await response.arrayBuffer();
        if (buffer.byteLength !== asset.bytes) {
            throw new Error(`${asset.path} failed byte validation (${buffer.byteLength} !== ${asset.bytes}).`);
        }
        const checksum = await this.sha256(buffer);
        if (checksum !== asset.sha256.toLowerCase()) {
            throw new Error(`${asset.path} failed SHA-256 validation.`);
        }
        const headers = new Headers(response.headers);
        headers.set("content-type", asset.mimeType);
        headers.set("content-length", String(asset.bytes));
        if (cache) {
            await cache.put(assetUrl, new Response(buffer.slice(0), { status: 200, headers }));
            await cache.put(markerUrl.href, new Response(JSON.stringify({
                bytes: asset.bytes,
                sha256: asset.sha256,
            }), { headers: { "content-type": "application/json" } }));
        }
        const blobUrl = URL.createObjectURL(new Blob([buffer], { type: asset.mimeType }));
        this.blobUrls.push(blobUrl);
        return { blobUrl, fromCache: Boolean(cached) };
    }
    async sha256(buffer) {
        if (!crypto.subtle)
            throw new Error("SHA-256 verification requires a secure browser context.");
        const digest = await crypto.subtle.digest("SHA-256", buffer);
        return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, "0")).join("");
    }
    async mountVariant(variant, urls) {
        const stage = document.createElement("div");
        stage.className = `ea-local-motion ${this.options.className}`.trim();
        stage.dataset.eaState = "loading";
        stage.style.cssText = [
            "position:relative",
            "overflow:hidden",
            "width:100%",
            "height:100%",
            `aspect-ratio:${variant.width}/${variant.height}`,
            "background:transparent",
            "isolation:isolate",
        ].join(";");
        const poster = document.createElement("img");
        poster.src = urls.poster.blobUrl;
        poster.alt = `${this.manifest?.resource ?? "Digital Human"} poster`;
        poster.decoding = "async";
        poster.style.cssText = this.layerStyle(1, this.visualTransitionMs());
        poster.style.zIndex = "0";
        const carrier = this.createVideo(urls.carrier.blobUrl, 0);
        carrier.style.zIndex = "1";
        const speaking = this.createVideo(urls.speaking.blobUrl, 0);
        speaking.style.zIndex = "3";
        const speakingStandby = this.createVideo(urls.speaking.blobUrl, 0);
        speakingStandby.style.zIndex = "2";
        const transitionCanvas = document.createElement("canvas");
        transitionCanvas.width = variant.width;
        transitionCanvas.height = variant.height;
        transitionCanvas.setAttribute("aria-hidden", "true");
        transitionCanvas.style.cssText = [
            this.layerStyle(0, this.visualTransitionMs()),
            "pointer-events:none",
            "z-index:4",
        ].join(";");
        carrier.loop = true;
        speaking.loop = false;
        speakingStandby.loop = false;
        speaking.playbackRate = this.effectiveSpeakingRate();
        speakingStandby.playbackRate = this.effectiveSpeakingRate();
        stage.append(poster, carrier, speakingStandby, speaking, transitionCanvas);
        if (this.options.plan === "free" && this.options.watermark) {
            stage.append(this.createFreeWatermark());
        }
        await Promise.all([
            this.waitForMedia(carrier),
            this.waitForMedia(speaking),
            this.waitForMedia(speakingStandby),
            this.waitForImage(poster),
        ]);
        this.clearSpeakingLoop();
        this.clearSpeakingVisualWatchdog();
        this.carrierVideo?.pause();
        this.speakingVideo?.pause();
        this.speakingStandbyVideo?.pause();
        for (const remove of this.runtimeListeners.splice(0))
            remove();
        this.container.replaceChildren(stage);
        this.stage = stage;
        this.posterImage = poster;
        this.carrierVideo = carrier;
        this.speakingVideo = speaking;
        this.speakingStandbyVideo = speakingStandby;
        this.transitionCanvas = transitionCanvas;
        this.installSpeakingVideoDiagnostics(speaking, "primary");
        this.installSpeakingVideoDiagnostics(speakingStandby, "standby");
        const carrierStarted = await carrier.play().then(() => true).catch(() => false);
        if (carrierStarted) {
            await this.waitForPaintedVideoFrame(carrier);
            carrier.pause();
            carrier.style.opacity = "1";
            await this.nextAnimationFrame();
            await new Promise((resolve) => window.setTimeout(resolve, this.visualTransitionMs() + 20));
            poster.style.opacity = "0";
            if (this.usesSharedNeutralLoop()) {
                carrier.pause();
                carrier.currentTime = 0;
            }
            else {
                await carrier.play().catch(() => undefined);
            }
        }
        stage.dataset.eaState = "idle";
    }
    createVideo(src, opacity) {
        const video = document.createElement("video");
        video.src = src;
        video.muted = true;
        video.autoplay = false;
        video.preload = "auto";
        video.playsInline = true;
        video.setAttribute("webkit-playsinline", "true");
        video.style.cssText = this.layerStyle(opacity, this.visualTransitionMs());
        return video;
    }
    speakingMotionWindow(video, profile) {
        const profileEnd = Number.isFinite(profile.motionEndSeconds) && profile.motionEndSeconds > 0
            ? profile.motionEndSeconds
            : Number.POSITIVE_INFINITY;
        const manifestDuration = this.manifest?.speakingDurationSeconds;
        const manifestEnd = Number.isFinite(manifestDuration) && (manifestDuration ?? 0) > 0
            ? manifestDuration
            : profileEnd;
        const mediaEnd = Number.isFinite(video.duration) && video.duration > 0
            ? video.duration
            : manifestEnd;
        const sourceEnd = Math.max(0.1, Math.min(profileEnd, manifestEnd, mediaEnd));
        const start = Math.min(Math.max(0, profile.motionStartSeconds), Math.max(0, sourceEnd - 0.1));
        // Keep playback away from the decoder's final frame. Browsers hold that
        // frame while an ended/restart event is scheduled, which is especially
        // visible when the generated mouth happens to be fully open.
        const endGuard = Math.min(0.08, Math.max(0.02, (sourceEnd - start) * 0.03));
        const end = Math.max(start + 0.05, sourceEnd - endGuard);
        return { start, end, duration: Math.max(0.05, end - start) };
    }
    clearSpeakingLoop() {
        if (this.speakingLoopTimer !== null) {
            window.clearTimeout(this.speakingLoopTimer);
            this.speakingLoopTimer = null;
        }
        this.speakingLoopTransitionInFlight = false;
        this.speakingLoopEpoch += 1;
    }
    installSpeakingVideoDiagnostics(video, role) {
        for (const eventName of ["waiting", "stalled", "suspend", "ended"]) {
            const handler = () => {
                this.emit("speaking-media-event", {
                    event: eventName,
                    role,
                    active: this.speakingVideo === video,
                    state: this.state,
                    currentTime: video.currentTime,
                    duration: video.duration,
                    playbackRate: video.playbackRate,
                    readyState: video.readyState,
                    networkState: video.networkState,
                    paused: video.paused,
                    visibilityState: document.visibilityState,
                });
                if (eventName === "ended"
                    && this.state === "speaking"
                    && this.speakingVideo === video
                    && !this.speakingLoopTransitionInFlight) {
                    this.speakingVisualProgress.forceStall(performance.now());
                }
            };
            video.addEventListener(eventName, handler);
            this.runtimeListeners.push(() => video.removeEventListener(eventName, handler));
        }
    }
    startSpeakingVisualWatchdog() {
        if (this.speakingVisualWatchdogTimer !== null || this.destroyed || this.state !== "speaking")
            return;
        const now = performance.now();
        this.speakingVisualProgress.reset(this.speakingVideo, this.speakingVideo?.currentTime ?? 0, now);
        this.speakingVisualWatchdogTickAt = now;
        this.scheduleSpeakingVisualWatchdogTick();
    }
    scheduleSpeakingVisualWatchdogTick() {
        if (this.destroyed || this.state !== "speaking")
            return;
        this.speakingVisualWatchdogTimer = window.setTimeout(() => {
            this.speakingVisualWatchdogTimer = null;
            this.checkSpeakingVisualProgress();
            this.scheduleSpeakingVisualWatchdogTick();
        }, SPEAKING_VISUAL_WATCHDOG_INTERVAL_MS);
    }
    clearSpeakingVisualWatchdog() {
        if (this.speakingVisualWatchdogTimer !== null) {
            window.clearTimeout(this.speakingVisualWatchdogTimer);
            this.speakingVisualWatchdogTimer = null;
        }
        this.speakingVisualProgress.clear();
        this.speakingVisualWatchdogTickAt = 0;
        this.speakingVisualRecoveryInFlight = false;
    }
    checkSpeakingVisualProgress() {
        const video = this.speakingVideo;
        const now = performance.now();
        const previousTickAt = this.speakingVisualWatchdogTickAt;
        this.speakingVisualWatchdogTickAt = now;
        if (!video || this.destroyed || this.state !== "speaking")
            return;
        if (this.mouthHeldClosedForOutputSilence) {
            this.speakingVisualProgress.reset(video, video.currentTime, now);
            return;
        }
        if (previousTickAt > 0 && now - previousTickAt > SPEAKING_VISUAL_WATCHDOG_INTERVAL_MS * 3) {
            this.emit("speaking-visual-main-thread-delay", { delayMs: now - previousTickAt });
        }
        if (document.visibilityState === "hidden") {
            this.speakingVisualProgress.reset(video, video.currentTime, now);
            return;
        }
        const stalledForMs = this.speakingVisualProgress.observe(video, video.currentTime, now, true);
        if (stalledForMs === null
            || this.speakingVisualRecoveryInFlight
            || this.speakingLoopTransitionInFlight)
            return;
        void this.recoverSpeakingVisual(video, stalledForMs);
    }
    async recoverSpeakingVisual(video, stalledForMs) {
        if (!this.languageProfile || this.speakingVideo !== video || this.state !== "speaking")
            return;
        this.speakingVisualRecoveryInFlight = true;
        const profile = this.languageProfile;
        const motion = this.speakingMotionWindow(video, profile);
        const elapsedSeconds = Math.max(SPEAKING_VISUAL_STALL_MS / 1_000, stalledForMs / 1_000);
        const audioTime = this.audioElement?.currentTime;
        const target = Number.isFinite(audioTime)
            ? this.phaseAlignedSpeakingTime(motion, audioTime, profile.motionRate)
            : motion.start + this.positiveRemainder(Math.max(0, video.currentTime - motion.start) + elapsedSeconds * Math.max(0.1, video.playbackRate || 1), motion.duration);
        this.emit("speaking-visual-stall-detected", {
            stalledForMs,
            currentTime: video.currentTime,
            targetTime: target,
            duration: video.duration,
            playbackRate: video.playbackRate,
            readyState: video.readyState,
            networkState: video.networkState,
            paused: video.paused,
        });
        const transitionEpoch = ++this.visualTransitionEpoch;
        const heldFrame = this.holdRenderedFrame(video);
        this.clearSpeakingLoop();
        try {
            video.pause();
            await this.seekVideoForTransition(video, target);
            if (this.destroyed || this.state !== "speaking" || this.speakingVideo !== video)
                return;
            video.playbackRate = this.effectiveSpeakingRate();
            await video.play();
            await this.waitForPaintedVideoFrame(video);
            await this.nextAnimationFrame();
            if (heldFrame)
                this.releaseHeldFrame(transitionEpoch);
            this.emit("speaking-visual-recovered", { stalledForMs, currentTime: video.currentTime });
            this.scheduleSpeakingLoop();
        }
        catch (cause) {
            const error = cause instanceof Error ? cause : new Error(String(cause));
            this.options.onError?.(error);
            this.emit("speaking-visual-recovery-failed", { message: error.message });
        }
        finally {
            this.speakingVisualProgress.reset(this.speakingVideo, this.speakingVideo?.currentTime ?? 0, performance.now());
            this.speakingVisualRecoveryInFlight = false;
        }
    }
    scheduleSpeakingLoop() {
        if (this.destroyed
            || this.state !== "speaking"
            || !this.speakingVideo
            || !this.speakingStandbyVideo
            || !this.languageProfile
            || this.speakingLoopTimer !== null
            || this.speakingLoopTransitionInFlight
            || this.mouthHeldClosedForOutputSilence)
            return;
        const active = this.speakingVideo;
        const motion = this.speakingMotionWindow(active, this.languageProfile);
        const rate = Math.max(0.1, active.playbackRate || 1);
        const crossfadeLeadMs = this.visualTransitionMs() + 80;
        const delayMs = Math.min(SPEAKING_VISUAL_WATCHDOG_INTERVAL_MS, Math.max(16, ((motion.end - Math.max(motion.start, active.currentTime)) / rate) * 1_000
            - crossfadeLeadMs));
        const epoch = this.speakingLoopEpoch;
        this.speakingLoopTimer = window.setTimeout(() => {
            this.speakingLoopTimer = null;
            const refreshedMotion = this.speakingMotionWindow(active, this.languageProfile);
            const refreshedRate = Math.max(0.1, active.playbackRate || 1);
            const remainingMs = ((refreshedMotion.end - active.currentTime) / refreshedRate) * 1_000;
            if (remainingMs > crossfadeLeadMs + 35) {
                this.scheduleSpeakingLoop();
                return;
            }
            this.speakingLoopTransitionInFlight = true;
            void this.crossfadeSpeakingLoop(active, epoch).finally(() => {
                if (epoch !== this.speakingLoopEpoch)
                    return;
                this.speakingLoopTransitionInFlight = false;
                this.scheduleSpeakingLoop();
            });
        }, delayMs);
    }
    async crossfadeSpeakingLoop(active, epoch) {
        const standby = this.speakingStandbyVideo;
        const profile = this.languageProfile;
        if (this.destroyed
            || epoch !== this.speakingLoopEpoch
            || this.state !== "speaking"
            || this.speakingVideo !== active
            || !standby
            || !profile)
            return;
        const standbyMotion = this.speakingMotionWindow(standby, profile);
        standby.pause();
        standby.currentTime = standbyMotion.start;
        standby.playbackRate = this.effectiveSpeakingRate();
        standby.style.opacity = "0";
        standby.style.zIndex = "2";
        try {
            await standby.play();
            await this.waitForPaintedVideoFrame(standby);
            await this.nextAnimationFrame();
        }
        catch (cause) {
            const error = cause instanceof Error ? cause : new Error(String(cause));
            this.options.onError?.(error);
            this.emit("playback-blocked", { message: error.message });
            return;
        }
        if (this.destroyed
            || epoch !== this.speakingLoopEpoch
            || this.state !== "speaking"
            || this.speakingVideo !== active
            || this.speakingStandbyVideo !== standby) {
            standby.pause();
            standby.style.opacity = "0";
            return;
        }
        active.style.zIndex = "2";
        standby.style.zIndex = "3";
        standby.style.opacity = "1";
        active.style.opacity = "0";
        this.speakingLoopTimer = window.setTimeout(() => {
            this.speakingLoopTimer = null;
            if (this.destroyed
                || epoch !== this.speakingLoopEpoch
                || this.state !== "speaking"
                || this.speakingVideo !== active
                || this.speakingStandbyVideo !== standby)
                return;
            active.pause();
            active.currentTime = this.speakingMotionWindow(active, profile).start;
            active.style.opacity = "0";
            this.speakingVideo = standby;
            this.speakingStandbyVideo = active;
            this.scheduleSpeakingLoop();
        }, this.visualTransitionMs() + 30);
    }
    createFreeWatermark() {
        const watermark = document.createElement("div");
        watermark.dataset.eaFreeWatermark = "true";
        watermark.setAttribute("aria-label", `${this.options.watermarkText} Free plan watermark`);
        watermark.style.cssText = [
            "align-items:center",
            "backdrop-filter:blur(8px)",
            "background:rgba(5,15,12,0.76)",
            `border:${FREE_PLAN_WATERMARK.borderWidth}px solid rgba(255,255,255,0.32)`,
            "border-radius:999px",
            `bottom:${FREE_PLAN_WATERMARK.bottomInset}px`,
            "color:white",
            "display:flex",
            `font-size:${FREE_PLAN_WATERMARK.fontSize}px`,
            "font-weight:800",
            `gap:${FREE_PLAN_WATERMARK.gap}px`,
            "left:50%",
            "letter-spacing:0.13em",
            "line-height:1",
            `padding:${FREE_PLAN_WATERMARK.verticalPadding}px ${FREE_PLAN_WATERMARK.horizontalPadding}px`,
            "pointer-events:none",
            "position:absolute",
            "text-transform:uppercase",
            "transform:translateX(-50%)",
            "white-space:nowrap",
            "z-index:10",
        ].join(";");
        const dot = document.createElement("span");
        dot.setAttribute("aria-hidden", "true");
        dot.style.cssText = [
            "background:#a4ff62",
            "border-radius:50%",
            `box-shadow:0 0 0 ${FREE_PLAN_WATERMARK.dotRingWidth}px rgba(164,255,98,0.18)`,
            `height:${FREE_PLAN_WATERMARK.dotSize}px`,
            `width:${FREE_PLAN_WATERMARK.dotSize}px`,
        ].join(";");
        watermark.append(dot, document.createTextNode(this.options.watermarkText));
        return watermark;
    }
    layerStyle(opacity, transitionMs) {
        return [
            "position:absolute",
            "inset:0",
            "width:100%",
            "height:100%",
            "display:block",
            "object-fit:cover",
            "object-position:50% 50%",
            "backface-visibility:hidden",
            "transform:translateZ(0)",
            `opacity:${opacity}`,
            `transition:opacity ${transitionMs}ms ease`,
        ].join(";");
    }
    visualTransitionMs() {
        const requested = this.manifest?.transitionMs ?? 160;
        return Math.min(240, Math.max(100, requested));
    }
    clearTransitionFrameTimer() {
        if (this.transitionFrameClearTimer !== null) {
            window.clearTimeout(this.transitionFrameClearTimer);
            this.transitionFrameClearTimer = null;
        }
    }
    holdRenderedFrame(video) {
        const canvas = this.transitionCanvas;
        if (!canvas
            || !video
            || video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA
            || video.videoWidth < 1
            || video.videoHeight < 1)
            return false;
        const context = canvas.getContext("2d", { alpha: false });
        if (!context)
            return false;
        this.clearTransitionFrameTimer();
        try {
            if (canvas.width !== video.videoWidth)
                canvas.width = video.videoWidth;
            if (canvas.height !== video.videoHeight)
                canvas.height = video.videoHeight;
            context.drawImage(video, 0, 0, canvas.width, canvas.height);
            canvas.style.transition = "none";
            canvas.style.opacity = "1";
            void canvas.offsetWidth;
            canvas.style.transition = `opacity ${this.visualTransitionMs()}ms ease`;
            return true;
        }
        catch {
            canvas.style.opacity = "0";
            return false;
        }
    }
    releaseHeldFrame(transitionEpoch) {
        const canvas = this.transitionCanvas;
        if (!canvas || transitionEpoch !== this.visualTransitionEpoch || this.destroyed)
            return;
        canvas.style.opacity = "0";
        this.clearTransitionFrameTimer();
        this.transitionFrameClearTimer = window.setTimeout(() => {
            this.transitionFrameClearTimer = null;
            if (transitionEpoch !== this.visualTransitionEpoch || !this.transitionCanvas)
                return;
            this.transitionCanvas.getContext("2d")?.clearRect(0, 0, this.transitionCanvas.width, this.transitionCanvas.height);
        }, this.visualTransitionMs() + 30);
    }
    releaseHeldFrameAfterCarrierPaint(transitionEpoch, alignedTimeSeconds) {
        const carrier = this.carrierVideo;
        if (!carrier) {
            this.releaseHeldFrame(transitionEpoch);
            return;
        }
        void (async () => {
            if (Number.isFinite(alignedTimeSeconds)) {
                carrier.pause();
                await this.seekVideoForTransition(carrier, alignedTimeSeconds);
            }
            if (transitionEpoch !== this.visualTransitionEpoch || this.destroyed)
                return;
            carrier.style.opacity = "1";
            await carrier.play().catch(() => undefined);
            await this.waitForPaintedVideoFrame(carrier);
            await this.nextAnimationFrame();
            if (transitionEpoch !== this.visualTransitionEpoch || this.destroyed)
                return;
            this.releaseHeldFrame(transitionEpoch);
            this.emit("carrier-frame-ready");
            this.finishSharedNeutralIdleMotion(carrier, transitionEpoch);
        })();
    }
    finishSharedNeutralIdleMotion(carrier, transitionEpoch) {
        if (!this.usesSharedNeutralLoop() || this.continuousFrameFeatures.length < 2)
            return;
        const frameCount = this.continuousFrameFeatures.length;
        const framesPerSecond = this.manifest?.continuousMotion?.framesPerSecond ?? 24;
        const currentIndex = Math.floor(Math.max(0, carrier.currentTime) * framesPerSecond) % frameCount;
        const requestedWindow = this.manifest?.idleNeutralMaxFrames ?? 12;
        const windowFrames = Math.min(frameCount - 1, Math.max(1, Math.round(requestedWindow)));
        let bestOffset = 1;
        let bestScore = Number.POSITIVE_INFINITY;
        for (let offset = 1; offset <= windowFrames; offset += 1) {
            const candidate = this.continuousFrameFeatures[(currentIndex + offset) % frameCount];
            const score = this.poseScore(candidate, "closed");
            if (score < bestScore) {
                bestScore = score;
                bestOffset = offset;
            }
            if (score <= 0.12)
                break;
        }
        carrier.playbackRate = 1;
        const targetIndex = (currentIndex + bestOffset) % frameCount;
        const delayMs = Math.max(24, (bestOffset / framesPerSecond) * 1000);
        window.setTimeout(() => {
            if (this.destroyed
                || transitionEpoch !== this.visualTransitionEpoch
                || this.state === "speaking"
                || this.carrierVideo !== carrier)
                return;
            carrier.pause();
            void this.seekVideoForTransition(carrier, targetIndex / framesPerSecond)
                .then(() => this.waitForPaintedVideoFrame(carrier))
                .then(() => {
                if (this.destroyed
                    || transitionEpoch !== this.visualTransitionEpoch
                    || this.state === "speaking"
                    || this.carrierVideo !== carrier)
                    return;
                this.emit("carrier-neutral-frame-ready", { frameIndex: targetIndex });
            });
        }, delayMs);
    }
    seekVideoForTransition(video, seconds) {
        const duration = Number.isFinite(video.duration) && video.duration > 0
            ? video.duration
            : 0;
        const target = duration > 0 ? this.positiveRemainder(seconds, duration) : Math.max(0, seconds);
        if (Math.abs(video.currentTime - target) < (1 / 60))
            return Promise.resolve();
        return new Promise((resolve) => {
            let settled = false;
            const finish = () => {
                if (settled)
                    return;
                settled = true;
                window.clearTimeout(timeoutHandle);
                video.removeEventListener("seeked", finish);
                resolve();
            };
            const timeoutHandle = window.setTimeout(finish, 300);
            video.addEventListener("seeked", finish, { once: true });
            try {
                video.currentTime = target;
            }
            catch {
                finish();
            }
        });
    }
    positiveRemainder(value, modulus) {
        return ((value % modulus) + modulus) % modulus;
    }
    phaseAlignedSpeakingTime(motion, audioTimeSeconds, motionRate) {
        const audioMotionTime = Math.max(0, audioTimeSeconds) * motionRate;
        const transitionAnchor = this.manifest?.transitionAnchorSeconds;
        if (Number.isFinite(transitionAnchor)) {
            const anchorPhase = this.positiveRemainder(transitionAnchor - motion.start, motion.duration);
            this.motionPhaseOffsetSeconds = this.positiveRemainder(anchorPhase - audioMotionTime, motion.duration);
            return motion.start + this.positiveRemainder(audioMotionTime + this.motionPhaseOffsetSeconds, motion.duration);
        }
        const carrierTime = this.carrierVideo?.currentTime;
        if (Number.isFinite(carrierTime)) {
            const carrierPhase = this.positiveRemainder(carrierTime - motion.start, motion.duration);
            this.motionPhaseOffsetSeconds = this.positiveRemainder(carrierPhase - audioMotionTime, motion.duration);
        }
        else {
            this.motionPhaseOffsetSeconds = 0;
        }
        return motion.start + this.positiveRemainder(audioMotionTime + this.motionPhaseOffsetSeconds, motion.duration);
    }
    nextAnimationFrame() {
        return new Promise((resolve) => requestAnimationFrame(() => resolve()));
    }
    waitForPaintedVideoFrame(video) {
        const frameVideo = video;
        if (typeof frameVideo.requestVideoFrameCallback !== "function") {
            return this.nextAnimationFrame().then(() => this.nextAnimationFrame());
        }
        return new Promise((resolve) => {
            let settled = false;
            let callbackHandle = 0;
            const finish = () => {
                if (settled)
                    return;
                settled = true;
                window.clearTimeout(timeoutHandle);
                resolve();
            };
            const timeoutHandle = window.setTimeout(() => {
                if (callbackHandle && typeof frameVideo.cancelVideoFrameCallback === "function") {
                    frameVideo.cancelVideoFrameCallback(callbackHandle);
                }
                finish();
            }, 350);
            callbackHandle = frameVideo.requestVideoFrameCallback(finish);
        });
    }
    waitForMedia(media) {
        if (media.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA)
            return Promise.resolve();
        return new Promise((resolve, reject) => {
            const ready = () => { cleanup(); resolve(); };
            const error = () => { cleanup(); reject(new Error(`Could not decode ${media.currentSrc || media.src}.`)); };
            const cleanup = () => {
                media.removeEventListener("loadeddata", ready);
                media.removeEventListener("error", error);
            };
            media.addEventListener("loadeddata", ready, { once: true });
            media.addEventListener("error", error, { once: true });
            media.load();
        });
    }
    async waitForImage(image) {
        if (!image.complete || image.naturalWidth < 1) {
            await new Promise((resolve, reject) => {
                image.addEventListener("load", () => resolve(), { once: true });
                image.addEventListener("error", () => reject(new Error(`Could not decode ${image.src}.`)), { once: true });
            });
        }
        if (typeof image.decode === "function") {
            await image.decode().catch(() => undefined);
        }
        await this.nextAnimationFrame();
    }
    startAudioSyncLoop() {
        this.stopAudioSyncLoop();
        if (!this.audioElement)
            return;
        const update = () => {
            if (this.state !== "speaking" || !this.audioElement || this.audioElement.paused)
                return;
            this.syncMotionToAudio(false);
            this.audioSyncFrame = requestAnimationFrame(update);
        };
        this.audioSyncFrame = requestAnimationFrame(update);
    }
    syncMotionToAudio(force) {
        if (!this.audioElement || !this.speakingVideo || !this.languageProfile)
            return;
        const profile = this.languageProfile;
        const motion = this.speakingMotionWindow(this.speakingVideo, profile);
        const expected = motion.start + this.positiveRemainder((Math.max(0, this.audioElement.currentTime) * profile.motionRate)
            + this.motionPhaseOffsetSeconds, motion.duration);
        if (force || Math.abs(this.speakingVideo.currentTime - expected) > 0.22) {
            const video = this.speakingVideo;
            const transitionEpoch = ++this.visualTransitionEpoch;
            const heldFrame = this.holdRenderedFrame(video);
            video.currentTime = expected;
            if (heldFrame) {
                void this.waitForPaintedVideoFrame(video)
                    .then(() => this.nextAnimationFrame())
                    .then(() => this.releaseHeldFrame(transitionEpoch));
            }
        }
    }
    stopAudioSyncLoop() {
        if (this.audioSyncFrame !== null)
            cancelAnimationFrame(this.audioSyncFrame);
        this.audioSyncFrame = null;
    }
    detachAudioElement(returnToIdle = true) {
        const wasSpeaking = this.state === "speaking";
        const wasAttached = this.audioElement !== null || this.audioListeners.length > 0;
        for (const remove of this.audioListeners.splice(0))
            remove();
        this.audioElement = null;
        if (returnToIdle && wasSpeaking)
            this.returnToIdle("audio-detached");
        if (wasAttached)
            this.emit("audio-detached");
    }
    detachAnalyser() {
        if (this.analyserFrame !== null)
            cancelAnimationFrame(this.analyserFrame);
        this.analyserFrame = null;
        this.analyser?.disconnect();
        this.analyser = null;
        this.analyserData = null;
        if (this.audioContext)
            void this.audioContext.close();
        this.audioContext = null;
    }
    prepareForListening() {
        this.holdSpeakingThroughSilence = false;
        this.mouthHeldClosedForOutputSilence = false;
        this.clearResetTimer();
        this.clearAudioHold();
        this.clearSpeakingLoop();
        this.clearSpeakingVisualWatchdog();
        this.stopAudioSyncLoop();
        const transitionEpoch = ++this.visualTransitionEpoch;
        if (this.audioElement && !this.audioElement.paused)
            this.audioElement.pause();
        if (this.speakingVideo && this.languageProfile) {
            const video = this.speakingVideo;
            const start = this.speakingMotionWindow(video, this.languageProfile).start;
            this.holdRenderedFrame(video);
            video.style.opacity = "0";
            if (this.speakingStandbyVideo) {
                this.speakingStandbyVideo.style.opacity = "0";
            }
            this.releaseHeldFrameAfterCarrierPaint(transitionEpoch, video.currentTime);
            window.setTimeout(() => {
                if (this.destroyed || this.state === "speaking")
                    return;
                video.pause();
                video.currentTime = start;
                if (this.speakingStandbyVideo && this.languageProfile) {
                    this.speakingStandbyVideo.pause();
                    this.speakingStandbyVideo.currentTime = this.speakingMotionWindow(this.speakingStandbyVideo, this.languageProfile).start;
                }
            }, this.visualTransitionMs() + 30);
        }
    }
    detachInputCapture(stopOwnedTracks) {
        if (this.inputAnalyserFrame !== null)
            cancelAnimationFrame(this.inputAnalyserFrame);
        this.inputAnalyserFrame = null;
        for (const remove of this.inputTrackListeners.splice(0))
            remove();
        this.inputSource?.disconnect();
        this.inputSource = null;
        this.inputAnalyser?.disconnect();
        this.inputAnalyser = null;
        this.inputAnalyserData = null;
        if (this.inputAudioContext)
            void this.inputAudioContext.close();
        this.inputAudioContext = null;
        if (stopOwnedTracks && this.stopInputTracksOnDetach) {
            for (const track of this.inputStream?.getTracks() ?? [])
                track.stop();
        }
        this.inputStream = null;
        this.stopInputTracksOnDetach = false;
        this.lastInputLevelEventAt = 0;
        this.options.onInputLevel?.(0);
    }
    clearResetTimer() {
        if (this.resetTimer !== null)
            window.clearTimeout(this.resetTimer);
        this.resetTimer = null;
    }
    clearAudioHold() {
        if (this.audioHoldTimer !== null)
            window.clearTimeout(this.audioHoldTimer);
        this.audioHoldTimer = null;
    }
    async configureContinuousMotion(asset) {
        this.resetContinuousMotion();
        if (!asset || this.manifest?.mode !== "continuous-speech-motion-v1")
            return;
        const response = await fetch(asset.blobUrl);
        if (!response.ok)
            throw new Error("Could not load the continuous motion frame index.");
        const value = await response.json();
        if (!Array.isArray(value) || value.length < 2) {
            throw new Error("The continuous motion frame index is empty.");
        }
        const decoded = value.map((item, index) => {
            if (!item || typeof item !== "object") {
                throw new Error(`Continuous motion frame ${index} is invalid.`);
            }
            const frame = item;
            const fields = ["openness", "width", "roundness", "brightness"];
            for (const field of fields) {
                if (typeof frame[field] !== "number" || !Number.isFinite(frame[field])) {
                    throw new Error(`Continuous motion frame ${index} has invalid ${field}.`);
                }
            }
            if (frame.index !== index)
                throw new Error(`Continuous motion frame ${index} is out of order.`);
            return {
                index,
                openness: frame.openness,
                width: frame.width,
                roundness: frame.roundness,
                brightness: frame.brightness,
            };
        });
        this.continuousFrameFeatures = this.normalizeContinuousFeatures(decoded);
        this.emit("continuous-motion-ready", {
            frameCount: this.continuousFrameFeatures.length,
            mapping: "realtime-transcript-rendered-audio",
        });
    }
    resetContinuousMotion() {
        this.continuousFrameFeatures = [];
        this.guidedMouthPoses = ["closed"];
        this.guidedPoseCursor = 0;
        this.lastGuidedTranscript = "";
        this.lastGuidedPoseAdvance = 0;
        this.smoothedContinuousRate = 0.82;
    }
    effectiveSpeakingRate() {
        return this.continuousFrameFeatures.length
            ? this.smoothedContinuousRate
            : (this.languageProfile?.motionRate ?? 1);
    }
    normalizeContinuousFeatures(features) {
        const range = (field) => {
            const values = features.map((feature) => feature[field]);
            const minimum = Math.min(...values);
            return { minimum, span: Math.max(0.000001, Math.max(...values) - minimum) };
        };
        const openness = range("openness");
        const width = range("width");
        const roundness = range("roundness");
        const brightness = range("brightness");
        const normalized = (value, bounds) => Math.min(1, Math.max(0, (value - bounds.minimum) / bounds.span));
        return features.map((feature) => ({
            index: feature.index,
            openness: normalized(feature.openness, openness),
            width: normalized(feature.width, width),
            roundness: normalized(feature.roundness, roundness),
            brightness: normalized(feature.brightness, brightness),
        }));
    }
    poseScore(frame, pose) {
        const targets = {
            closed: [0, 0.45, 0.15, 0],
            rest: [0.06, 0.42, 0.18, 0.08],
            slight: [0.24, 0.45, 0.3, 0.15],
            medium: [0.52, 0.5, 0.52, 0.35],
            wide: [0.96, 0.55, 0.82, 0.42],
            o: [0.68, 0.08, 0.98, 0.18],
            smile: [0.18, 0.96, 0.08, 0.12],
            ee: [0.38, 0.96, 0.18, 0.96],
        };
        const weights = {
            closed: [1, 0.2, 0.2, 0.1],
            rest: [1.4, 0.2, 0.2, 0.15],
            slight: [1.3, 0.2, 0.25, 0.15],
            medium: [1.2, 0.25, 0.3, 0.15],
            wide: [1.4, 0.15, 0.35, 0.1],
            o: [0.8, 0.9, 1.1, 0.15],
            smile: [0.8, 1.1, 0.7, 0.45],
            ee: [0.65, 0.85, 0.45, 0.9],
        };
        const target = targets[pose];
        const weight = weights[pose];
        const values = [frame.openness, frame.width, frame.roundness, frame.brightness];
        const total = weight.reduce((sum, item) => sum + item, 0);
        return values.reduce((sum, item, index) => sum + Math.abs(item - target[index]) * weight[index], 0) / total;
    }
    guidedMouthProgram(text) {
        const normalized = text.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
        const characters = [...normalized];
        const poses = ["closed"];
        for (let index = 0; index < characters.length;) {
            const character = characters[index];
            if (/\s|[.,!?;:]/.test(character)) {
                poses.push("closed");
                index += 1;
                continue;
            }
            const pair = characters.slice(index, index + 2).join("");
            if (["oo", "ou", "ow", "oa", "qu", "wh"].includes(pair)) {
                poses.push("o");
                index += 2;
                continue;
            }
            if (["ee", "ea", "ie", "ei", "ey"].includes(pair)) {
                poses.push("ee");
                index += 2;
                continue;
            }
            if (["sh", "ch", "zh"].includes(pair)) {
                poses.push("smile");
                index += 2;
                continue;
            }
            if (pair === "th") {
                poses.push("slight");
                index += 2;
                continue;
            }
            if (pair === "ph") {
                poses.push("ee");
                index += 2;
                continue;
            }
            if (/[bmp]/.test(character))
                poses.push("closed");
            else if (/[ouwq]/.test(character))
                poses.push("o");
            else if (/[eiyfv]/.test(character))
                poses.push("ee");
            else if (character === "a")
                poses.push("medium");
            else if (/[csxzj]/.test(character))
                poses.push("smile");
            else if (/[hrlndtgk]/.test(character))
                poses.push("rest");
            else
                poses.push("slight");
            index += 1;
        }
        poses.push("closed");
        return poses;
    }
    fallbackPose(energy, phase) {
        const poses = energy < 0.12
            ? ["rest", "slight"]
            : energy < 0.35
                ? ["slight", "medium", "ee", "medium"]
                : energy < 0.68
                    ? ["medium", "smile", "ee", "o"]
                    : ["medium", "wide", "o", "ee"];
        return poses[Math.abs(phase) % poses.length];
    }
    transition(next) {
        const previous = this.state;
        if (previous === next)
            return;
        this.state = next;
        if (this.stage)
            this.stage.dataset.eaState = next;
        this.options.onStateChange?.(next, previous);
    }
    emit(type, detail) {
        this.options.onEvent?.({ type, at: performance.now(), detail });
    }
    assertReady() {
        this.assertActive();
        if (!this.settings || !this.manifest || !this.resolution) {
            throw new Error("Initialize the Experts Alive lip-sync runtime first.");
        }
    }
    assertActive() {
        if (this.destroyed)
            throw new Error("The Experts Alive lip-sync runtime has been destroyed.");
    }
}
export function createExpertsAliveLipSync(options) {
    return new ExpertsAliveLipSyncRuntime(options);
}
